/**
 * Utility to detect multi-collection trades (anti-spoofing).
 * Warns users when a trade contains NFTs from different collections,
 * which could indicate a fake/copycat NFT being slipped in.
 */

interface ItemWithCollection {
  collection_id?: string | null;
  collection_name?: string | null;
}

export interface MultiCollectionResult {
  hasMultipleCollections: boolean;
  hasUnknownCollection: boolean;
}

/**
 * Check if a flat list of items spans multiple collections.
 * Uses collection_id as primary discriminator, falls back to collection_name.
 * Null/undefined collection is treated as "unknown" and triggers warning.
 */
export function detectMultiCollection(items: ItemWithCollection[]): MultiCollectionResult {
  if (items.length <= 1) {
    const hasUnknown = items.length === 1 && !items[0].collection_id && !items[0].collection_name;
    return { hasMultipleCollections: false, hasUnknownCollection: hasUnknown };
  }

  const collectionKeys = new Set<string>();
  let hasUnknownCollection = false;

  for (const item of items) {
    const key = item.collection_id || item.collection_name;
    if (key) {
      collectionKeys.add(key);
    } else {
      hasUnknownCollection = true;
    }
  }

  const distinctCount = collectionKeys.size + (hasUnknownCollection ? 1 : 0);

  return {
    hasMultipleCollections: distinctCount > 1,
    hasUnknownCollection,
  };
}

/**
 * Convenience wrapper: merges both sides of a trade and checks for multi-collection.
 */
export function detectMultiCollectionInTrade(
  sideA: ItemWithCollection[],
  sideB: ItemWithCollection[],
): MultiCollectionResult {
  return detectMultiCollection([...sideA, ...sideB]);
}
